import i18n, { getDateLocale } from '@/i18n';
import { SupportTicket } from '@haccp/api-client';

/**
 * Get CSS class for support ticket status badge
 */
export function getSupportStatusClass(status: SupportTicket['status']): string {
  switch (status) {
    case 'OPEN':
      return 'bg-blue-50 text-blue-700 border border-blue-200';
    case 'IN_PROGRESS':
      return 'bg-yellow-50 text-yellow-700 border border-yellow-200';
    case 'CLOSED':
      return 'bg-green-50 text-green-700 border border-green-200';
    default:
      return 'bg-neutral-50 text-neutral-700 border border-neutral-200';
  }
}

/**
 * Get human-readable support status text
 */
export function getSupportStatusText(status: SupportTicket['status']): string {
  return i18n.t(`support.statusText.${status}`, { defaultValue: status });
}

/**
 * Get CSS class for support ticket category badge
 */
export function getSupportCategoryClass(category: SupportTicket['category']): string {
  switch (category) {
    case 'PAYMENT':
      return 'bg-purple-50 text-purple-700 border border-purple-200';
    case 'CONFIGURATION':
      return 'bg-cyan-50 text-cyan-700 border border-cyan-200';
    case 'BUG':
      return 'bg-red-50 text-red-700 border border-red-200';
    case 'OTHER':
      return 'bg-neutral-50 text-neutral-700 border border-neutral-200';
    default:
      return 'bg-neutral-50 text-neutral-700 border border-neutral-200';
  }
}

/**
 * Get human-readable support category text
 */
export function getSupportCategoryText(category: SupportTicket['category']): string {
  return i18n.t(`support.categoryText.${category}`, { defaultValue: category });
}

/**
 * Format support date for display
 */
export function formatSupportDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(getDateLocale(i18n.language), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format support date and time
 */
export function formatSupportDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString(getDateLocale(i18n.language), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get support ticket categories for dropdown (translated at call time)
 */
export function getSupportCategories() {
  return [
    { value: 'PAYMENT', label: i18n.t('support.categoryLabel.PAYMENT') },
    { value: 'CONFIGURATION', label: i18n.t('support.categoryLabel.CONFIGURATION') },
    { value: 'BUG', label: i18n.t('support.categoryLabel.BUG') },
    { value: 'OTHER', label: i18n.t('support.categoryLabel.OTHER') },
  ] as const;
}

export const SUPPORT_CATEGORIES = [
  { value: 'PAYMENT', label: 'Problème de paiement' },
  { value: 'CONFIGURATION', label: 'Aide à la configuration' },
  { value: 'BUG', label: 'Signalement de bug' },
  { value: 'OTHER', label: 'Autre' },
] as const;

/**
 * Validate support ticket form
 */
export function validateSupportForm(category: string, message: string): { isValid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  if (!category) {
    errors.category = i18n.t('support.errors.categoryRequired');
  }

  if (!message || message.trim().length === 0) {
    errors.message = i18n.t('support.errors.messageRequired');
  } else if (message.trim().length < 10) {
    errors.message = i18n.t('support.errors.messageTooShort');
  } else if (message.trim().length > 2000) {
    errors.message = i18n.t('support.errors.messageTooLong');
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}
